import { get, post, put, del } from '../http'

/**
 * 获取公司/工作室列表
 * @param {Object} params 查询参数
 * @param {string} params.name 公司名称（模糊查询）
 * @param {number} params.page 页码
 * @param {number} params.pageSize 每页数量
 * @returns {Promise<Object>} 返回 { list: [...], total, page, pageSize, totalPages }
 */
export const getCompanyList = (params = {}) => {
  return get('/api/companies', params)
}

/**
 * 获取公司/工作室详情
 * @param {number} id 公司ID
 * @returns {Promise<Object>} 返回公司详情
 */
export const getCompanyDetail = (id) => {
  return get(`/api/companies/${id}`)
}

/**
 * 创建公司/工作室
 * @param {Object} data 公司数据
 * @param {string} data.name 公司名称（必填）
 * @param {string} data.address 地址（必填）
 * @param {string} data.phone 联系电话（必填）
 * @param {string} data.contactPerson 联系人（必填）
 * @param {string} data.emergencyContact 紧急联系方式（必填）
 * @returns {Promise<Object>} 返回创建结果
 */
export const createCompany = (data) => {
  return post('/api/companies', data)
}

/**
 * 更新公司/工作室
 * @param {number} id 公司ID
 * @param {Object} data 公司数据
 * @returns {Promise<Object>} 返回更新结果
 */
export const updateCompany = (id, data) => {
  return put(`/api/companies/${id}`, data)
}

/**
 * 删除公司/工作室
 * @param {number} id 公司ID
 * @returns {Promise<Object>} 返回删除结果
 */
export const deleteCompany = (id) => {
  return del(`/api/companies/${id}`)
}

/**
 * 获取公司下的角色列表
 * @param {number} companyId 公司ID
 * @returns {Promise<Array>} 返回角色列表
 */
export const getCompanyRoles = (companyId) => {
  return get(`/api/companies/${companyId}/roles`)
}

/**
 * 创建角色
 * @param {number} companyId 公司ID
 * @param {Object} data 角色数据
 * @param {string} data.name 角色名称（必填）
 * @returns {Promise<Object>} 返回创建结果
 */
export const createRole = (companyId, data) => {
  return post(`/api/companies/${companyId}/roles`, data)
}

/**
 * 更新角色
 * @param {number} companyId 公司ID
 * @param {number} roleId 角色ID
 * @param {Object} data 角色数据
 * @returns {Promise<Object>} 返回更新结果
 */
export const updateRole = (companyId, roleId, data) => {
  return put(`/api/companies/${companyId}/roles/${roleId}`, data)
}

/**
 * 删除角色
 * @param {number} companyId 公司ID
 * @param {number} roleId 角色ID
 * @returns {Promise<Object>} 返回删除结果
 */
export const deleteRole = (companyId, roleId) => {
  return del(`/api/companies/${companyId}/roles/${roleId}`)
}

/**
 * 更新角色权限
 * @param {number} companyId 公司ID
 * @param {number} roleId 角色ID
 * @param {Object} data 权限数据
 * @param {Array<string>} data.permissions 权限列表
 * @returns {Promise<Object>} 返回更新结果
 */
export const updateRolePermissions = (companyId, roleId, data) => {
  return put(`/api/companies/${companyId}/roles/${roleId}/permissions`, data)
}

/**
 * 获取角色权限
 * @param {number} companyId 公司ID
 * @param {number} roleId 角色ID
 * @returns {Promise<Array>} 返回权限列表
 */
export const getRolePermissions = (companyId, roleId) => {
  return get(`/api/companies/${companyId}/roles/${roleId}/permissions`)
}

/**
 * 获取角色人员列表
 * @param {number} companyId 公司ID
 * @param {number} roleId 角色ID
 * @returns {Promise<Array>} 返回人员列表
 */
export const getRoleMembers = (companyId, roleId) => {
  return get(`/api/companies/${companyId}/roles/${roleId}/members`)
}

/**
 * 添加角色人员
 * @param {number} companyId 公司ID
 * @param {number} roleId 角色ID
 * @param {Object} data 人员数据
 * @param {string} data.identifier 标识符（账号/QQ/手机号码）
 * @param {string} data.type 类型：'username' | 'qq' | 'phone'
 * @returns {Promise<Object>} 返回添加结果
 */
export const addRoleMember = (companyId, roleId, data) => {
  return post(`/api/companies/${companyId}/roles/${roleId}/members`, data)
}

/**
 * 移除角色人员
 * @param {number} companyId 公司ID
 * @param {number} roleId 角色ID
 * @param {number} memberId 人员ID
 * @returns {Promise<Object>} 返回移除结果
 */
export const removeRoleMember = (companyId, roleId, memberId) => {
  return del(`/api/companies/${companyId}/roles/${roleId}/members/${memberId}`)
}

/**
 * 查询参与过指定公司作品的所有人员清单
 * @param {number} companyId 公司ID
 * @returns {Promise<Object>} 返回参与人员列表
 */
export const getCompanyParticipants = (companyId) => {
  return get(`/api/companies/${companyId}/participants`)
}

